/**
 * @class SoulogExpertiseScroll
 * Recreates sharebien featured-expertises' "stacked video reveal" effect (pure JS, no GSAP).
 *
 * Desktop (≥1024px): inside the left sticky frame, N videos are absolutely stacked (increasing z-index). Video 0 is the always-visible base;
 * from video e≥1 on, progress p∈[0,1] is derived from the top edge of the e-th text item on the right rising from "frame top + its own height" to "frame top",
 * i.e. at the instant p=1 (video fully revealed), the item's top edge is exactly flush with the frame top -- the text rests fully at the top right;
 * driven by the same transforms as sharebien (yPercent implemented equivalently with CSS translateY percentages):
 *   media[e]           : translateY(100%→0)      -- the whole video slides up from below to cover the previous one
 *   child[e]           : translateY(-100%→0)     -- inner counter-offset = mask reveal (content visually stays put)
 *   content[e] own settle: translateY(60%→0)     -- the new video content settles with parallax
 *   content[e-1] pushed : translateY(0→-60%)      -- the previous video content moves up with parallax (the two terms add; boundaries don't interfere)
 *   overlay[e-1]       : opacity(0→0.75)         -- dim the previous video as it gets covered
 * Mobile (<1024px): each item carries its own inline video and fades in when entering the viewport; no stacking animation.
 * prefers-reduced-motion: no scrub; only the base video + items always visible.
 * Shopify compatibility: scoped to the section root, re-initialized in connectedCallback, cleaned up in disconnectedCallback.
 */
if (!customElements.get("soulog-expertise-scroll")) {
	const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

	class SoulogExpertiseScroll extends HTMLElement {
		connectedCallback() {
			this.items = Array.from(this.querySelectorAll(".esx__item"));
			this.medias = Array.from(this.querySelectorAll(".esx__media"));
			this.childs = Array.from(this.querySelectorAll(".esx__media-child"));
			this.contents = Array.from(this.querySelectorAll(".esx__media-content"));
			this.overlays = Array.from(this.querySelectorAll(".esx__media-overlay"));
			this.videos = Array.from(this.querySelectorAll(".esx__video"));
			this.mq = window.matchMedia("(min-width: 1024px)");
			this.reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
			this.ticking = false;
			this.io = null;

			this.onScroll = () => this.requestUpdate();
			this.onResize = () => {
				this.measure();
				this.requestUpdate();
			};
			this.onMediaChange = () => this.setup();

			if (this.mq.addEventListener) this.mq.addEventListener("change", this.onMediaChange);
			else if (this.mq.addListener) this.mq.addListener(this.onMediaChange);

			this.setAttribute("data-ready", "");
			this.setup();
		}

		setup() {
			this.teardown();

			// Mobile: items fade in when entering the viewport; videos are inline per item, no stacking
			if (!this.mq.matches) {
				this.resetTransforms();
				this.io = new IntersectionObserver(
					(entries) => {
						for (const e of entries) {
							if (e.isIntersecting) {
								e.target.classList.add("is-inview");
								this.io.unobserve(e.target);
							}
						}
					},
					{ rootMargin: "0px 0px -12% 0px", threshold: 0.15 },
				);
				this.items.forEach((el) => this.io.observe(el));
				this.playAll();
				return;
			}

			// Desktop: scrub stacking
			this.items.forEach((el) => el.classList.add("is-inview"));
			if (this.reduce.matches) {
				this.resetTransforms(); // show only the base video, no switching on scroll
				return;
			}
			this.measure();
			window.addEventListener("scroll", this.onScroll, { passive: true });
			window.addEventListener("resize", this.onResize);
			this.playAll();
			this.update();
		}

		// Distance from the viewport top once the frame is stuck (CSS --esx-sticky-top, includes header height).
		// It is the reference line for reveal progress and doesn't change while scrolling; read it only on setup / resize to avoid a per-frame getComputedStyle.
		measure() {
			const sticky = this.querySelector(".esx__sticky");
			this.stickyTop = sticky ? parseFloat(getComputedStyle(sticky).top) || 0 : 0;

			// Column-tail slack: at the moment the last item's top edge rises to the frame top (= last video fully revealed), the frame must still be stuck,
			// otherwise it slides away with the column tail first and leaves before the last video is fully revealed. Required slack = frame height − last item height;
			// 0 when the item is taller than the frame -- sticky's own height difference is enough then, and any extra only makes the text scroll up for nothing.
			const tail = this.querySelector(".esx__list-tail");
			const frame = this.querySelector(".esx__frame");
			const last = this.items[this.items.length - 1];
			if (tail && frame && last) {
				const need = frame.getBoundingClientRect().height - last.getBoundingClientRect().height;
				tail.style.height = `${Math.max(0, Math.round(need))}px`;
			}
		}

		// Reset to the initial state: base (0) in place, the rest hidden below the frame
		resetTransforms() {
			this.medias.forEach((m, i) => {
				const child = this.childs[i];
				const content = this.contents[i];
				const overlay = this.overlays[i];
				if (i === 0) {
					if (m) m.style.transform = "translateY(0%)";
					if (child) child.style.transform = "translateY(0%)";
				} else {
					if (m) m.style.transform = "translateY(100%)";
					if (child) child.style.transform = "translateY(-100%)";
				}
				if (content) content.style.transform = "translateY(0%)";
				if (overlay) overlay.style.opacity = "0";
			});
		}

		requestUpdate() {
			if (this.ticking) return;
			this.ticking = true;
			requestAnimationFrame(() => {
				this.ticking = false;
				this.update();
			});
		}

		// Drive each video's stacking transform from its item's progress in the viewport
		update() {
			const top = this.stickyTop || 0;
			const n = this.medias.length;
			// Each item's progress p[e] (only e≥1 drives a video): the travel of its top edge from top+height up to top maps to 0→1.
			// At p=1 the item's top edge == frame top, and at that moment the next item's top edge is exactly at top+height (p[e+1]=0), so they hand off seamlessly.
			const p = new Array(n).fill(0);
			for (let e = 1; e < n; e++) {
				const item = this.items[e];
				if (!item) continue;
				const r = item.getBoundingClientRect();
				p[e] = r.height ? clamp((top + r.height - r.top) / r.height, 0, 1) : 0;
			}

			for (let e = 1; e < n; e++) {
				const pe = p[e];
				if (this.medias[e]) this.medias[e].style.transform = `translateY(${100 * (1 - pe)}%)`;
				if (this.childs[e]) this.childs[e].style.transform = `translateY(${-100 * (1 - pe)}%)`;
			}

			// content[k] = own settle (60→0 by p[k]) + pushed away by the next one (0→-60 by p[k+1]); at a boundary one of the two terms is 0
			for (let k = 0; k < n; k++) {
				const settle = k >= 1 ? 60 * (1 - p[k]) : 0;
				const away = k + 1 < n ? -60 * p[k + 1] : 0;
				if (this.contents[k]) this.contents[k].style.transform = `translateY(${settle + away}%)`;
				// overlay[k] is dimmed when covered by the next video
				if (this.overlays[k]) this.overlays[k].style.opacity = String(k + 1 < n ? 0.75 * p[k + 1] : 0);
			}
		}

		playAll() {
			const list = this.mq.matches ? this.videos : this.querySelectorAll(".esx__item-media video");
			list.forEach((v) => {
				const play = v.play && v.play();
				if (play && play.catch) play.catch(() => {});
			});
		}

		teardown() {
			if (this.io) {
				this.io.disconnect();
				this.io = null;
			}
			window.removeEventListener("scroll", this.onScroll);
			window.removeEventListener("resize", this.onResize);
		}

		disconnectedCallback() {
			this.teardown();
			if (this.mq) {
				if (this.mq.removeEventListener) this.mq.removeEventListener("change", this.onMediaChange);
				else if (this.mq.removeListener) this.mq.removeListener(this.onMediaChange);
			}
		}
	}
	customElements.define("soulog-expertise-scroll", SoulogExpertiseScroll);
}
